import {createAsyncThunk} from '@reduxjs/toolkit';
import {JOBFEED_SLICE_NAME} from '@jobseeker/common-core/constants';
import {axiosInterceptorInstance} from '@jobseeker/common-core/helpers';
import {AxiosError} from 'axios';
import {FollowUpJobRequest, JobDetailRequest} from './requestInterfaces';
import jobDetailService from '@jobseeker/common-core/services/jobdetail.service';

const axios = axiosInterceptorInstance(
  `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/${process.env.NEXT_PUBLIC_PREFIX_JOB_SERVICE}`
);

export const fetchJobDetails = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/fetchJobDetails`,
  async ({jobId, ...params}: JobDetailRequest, ThunkService) => {
    try {
      const headers = {
        referer: `https://www.${process.env.NEXT_PUBLIC_APP_DOMAIN}/`,
      };
      const jobInfo = await jobDetailService.getJobDetails({
        jobId,
        headers,
        ...params,
      });
      return jobInfo;
    } catch (error) {
      const err = error as AxiosError;
      console.error(err);
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

const axiosUser = axiosInterceptorInstance(
  `${process.env.NEXT_PUBLIC_USER_SERVICE_HOST}${process.env.NEXT_PUBLIC_USER_SERVICE_VERSION}`
);

export const followUpJob = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/followUpJob`,
  async ({userId, jobId, ...params}: FollowUpJobRequest, ThunkService) => {
    try {
      const response = await axiosUser.put(
        `/user/${userId}/followup/${jobId}`,
        {
          params,
        }
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error(err);
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const getAppliedJobCount = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/appliedJobCount`,
  async (params: any, ThunkService) => {
    try {
      const response = await jobDetailService.getAppliedJobCount(params);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      console.error(err);
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchJobInsights = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/fetchJobInsights`,
  async ({userId, jobId, ...params}: FollowUpJobRequest, ThunkService) => {
    try {
      const response = await axios.get(`/insights`, {
        params: {jobcode: jobId, ...params},
      });
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error(err);
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

type OldUserServiceMagicRankApiResponse = {
  success: number;
  message: string;
  applications?: number;
  magicRank?: string; // e.g. "<b>249</b> (25.23 percentile)"
};

type AngelMagicApiResponse =
  | {
      magicRank: {
        rank: number;
        percentile: string;
        upgradeRankFlag: number;
        upgradeRank: number;
        upgradePercentile: string;
        upgradeRankText: string;
      };
      applications: string;
      success: number;
      status: number;
      message: string | null;
      error: number;
      error_msg: string;
      notify: null;
      logout: string;
      promember_price: string;
    }
  | {
      error: number;
      success: number;
      error_msg: string;
      status: number;
      message: null;
      notify: null;
      logout: string;
      promember_price: string;
    };

const transformMagicApiResponse = (
  input: OldUserServiceMagicRankApiResponse
): AngelMagicApiResponse => {
  if (input.success === 0 || !input?.magicRank || !input.applications) {
    return {
      error: 1,
      success: 0,
      error_msg: input.message,
      status: 700,
      message: null,
      notify: null,
      logout: '',
      promember_price: '500',
    };
  }

  const match = input.magicRank.match(/<b>(\d+)<\/b>\s+\(([^)]+)\)/);

  const rank = match ? parseInt(match[1], 10) : 0;
  const percentile = match ? match[2] : '0 percentile';

  return {
    magicRank: {
      rank,
      percentile,
      upgradeRankFlag: 0,
      upgradeRank: 0,
      upgradePercentile: '',
      upgradeRankText: '',
    },
    applications: input.applications.toString(),
    success: input.success,
    status: 200,
    message: input.message || null,
    error: 0,
    error_msg: '',
    notify: null,
    logout: '',
    promember_price: '500',
  };
};

export const getMagicRank = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/fetchMagicRank`,
  async ({jobId, userId = 0, ...params}: JobDetailRequest, ThunkService) => {
    try {
      const response = await axios.get(`/magic-rank`, {
        params: {jobId, ...params},
      });

      if (response?.data?.status?.code !== 200 || response?.data?.data?.error) {
        throw ThunkService.rejectWithValue(response?.data);
      }

      return response?.data?.data;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);
